// Single source of truth for the app's keyboard-focus indicator: a 2px algae
// ring with no offset, and the native outline suppressed. Apply to every
// custom interactive element (buttons, links, table headers, radios) so
// focus styling stays consistent instead of falling back to browser/plugin
// defaults on a per-component basis.
export const FOCUS_VISIBLE_RING = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-algae-500'

// For elements needing a visible "halo" gap around the ring - e.g. a
// same-colour ring butting onto a same-colour border/fill (the Primary
// button), or a control sitting on a dark sidebar/pagination panel. This uses
// a native `outline` + `outline-offset`, not Tailwind's `ring-offset`: a ring
// offset is a solid box-shadow that must be painted in a colour matching
// whatever's actually behind the element, and any mismatch (a different page
// background, or reuse in a new context) shows up as a visible mismatched
// box instead of a clean gap. An outline's offset gap is genuinely empty
// space, so it always reveals the real background rather than a guessed-at
// colour. Use this instead of `ring-offset` for any halo-style focus ring.
export const FOCUS_VISIBLE_RING_OFFSET =
	'focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-algae-500 focus-visible:outline-offset-2'

// For tab-style controls with a bottom border/underline: an inset ring stays
// inside the element's padding edge so it does not extend below the active
// underline and clash with it, while still giving a clear focus boundary. The
// ring itself keeps the standard 2px width; 'smaller' is achieved by rounding
// the ring to match the tab shape so it sits tighter inside.
export const FOCUS_VISIBLE_RING_INSET = `${FOCUS_VISIBLE_RING} focus-visible:ring-inset focus-visible:rounded-sm`

// Same inset treatment but with breathing room for plain underlined text links,
// so the algae ring does not sit directly on the text glyphs.
export const TEXT_LINK_FOCUS_RING = `inline-block rounded p-1 -m-1 ${FOCUS_VISIBLE_RING_INSET}`

// For underlined tab controls (border-b-2 active indicator): a ring (box-shadow)
// either competes with the border for the same edge (inset) or bleeds outward into
// neighbouring tabs/content (offset), since these tabs sit flush against other page
// elements with no reserved whitespace. A native outline pulled inward with a
// negative offset avoids both - it stays fully inside the button, clear of the
// border, without needing extra layout gaps. No border-radius is applied: these
// tabs are square-cornered already, and border-radius rounds the element's own
// border along with the outline, which would make the permanent underline's
// corners pop between square and rounded as focus moves on/off the tab.
export const FOCUS_VISIBLE_OUTLINE_INSET =
	'focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-algae-500 focus-visible:outline-offset-[-6px]'

// Applied to text inputs, selects, and other editable fields so they show the
// same algae highlight on focus (mouse or keyboard) and have no resting-state
// border, matching the SearchInput and SearchableSelect patterns.
export const INPUT_FOCUS_RING = 'focus:outline-none focus:ring-2 focus:ring-algae-500 focus:border-algae-500 border-transparent'

// For native radios and checkboxes styled by @tailwindcss/forms. The plugin
// hardcodes its own [type=radio]:focus / [type=checkbox]:focus rule, so the
// override must live on the same :focus pseudo-class (not :focus-visible) to
// win on specificity and apply consistently for both mouse and keyboard.
export const FORM_CONTROL_FOCUS_RING = 'focus:outline-none focus:ring-2 focus:ring-algae-500 focus:ring-offset-0'
